import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Pencil, Plus, Trash2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useTheme } from '@/client/theme/theme-provider';
import { useDeleteOtherIncomeMutation, useRevenueQuery, useUpsertOtherIncomeMutation } from '@/client/api/business/spend';
import { INCOME_KIND_LABEL, REVENUE_LINES, type OtherIncome, type RevenueLine } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, filterClass, inputClass, today } from './components/spend-ui';
import { DeleteConfirmDialog, EmptyState, Panel, RowMenu } from '../HumanResource/components/hr-ui';
import { money } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shift = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + by, 1)).toISOString().slice(0, 7);
};
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
const RANGES = [3, 6, 12] as const;

type IncomeForm = { id?: string; receivedOn: string; kind: OtherIncome['kind']; source: string; description: string; amount: string; currency: string; reference: string };

/** What FeelyFeely earned: platform sales read live from payments (refunds
 *  taken off in the month they happened), marketplace commission, and income
 *  entered by hand. One currency per chart; nothing is converted. */
export default function RevenuePage() {
  const [span, setSpan] = useState<(typeof RANGES)[number]>(6);
  const to = thisMonth();
  const from = shift(to, -(span - 1));
  const q = useRevenueQuery(from, to);
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const upsert = useUpsertOtherIncomeMutation();
  const remove = useDeleteOtherIncomeMutation();
  const [form, setForm] = useState<IncomeForm | null>(null);
  const [deleting, setDeleting] = useState<OtherIncome | null>(null);

  const data = q.data;
  const currencies = useMemo(() => [...new Set((data?.rows ?? []).map((r) => r.currency))].sort(), [data]);
  const [picked, setPicked] = useState<string | null>(null);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0] ?? 'GHS';

  const lines = REVENUE_LINES.filter((l) => (data?.rows ?? []).some((r) => r.currency === currency && r.line === l.id));
  const chart = useMemo(
    () =>
      (data?.months ?? []).map((m) => {
        const point: Record<string, number | string> = { month: m, label: monthLabel(m) };
        for (const l of REVENUE_LINES) {
          const row = data?.rows.find((r) => r.month === m && r.line === l.id && r.currency === currency);
          point[l.id] = row ? row.net : 0;
        }
        return point;
      }),
    [data, currency],
  );
  const total = data?.totals.find((t) => t.currency === currency);
  const net = (m: string) => (data?.rows ?? []).filter((r) => r.month === m && r.currency === currency).reduce((n, r) => n + r.net, 0);
  const cur = net(to);
  const prev = net(shift(to, -1));
  const change = prev ? ((cur - prev) / Math.abs(prev)) * 100 : null;

  const stats = [
    { key: 'net', label: `Net revenue, ${span} months`, value: total ? money(total.net, currency) : '—' },
    { key: 'month', label: 'This month so far', value: money(cur, currency) },
    { key: 'change', label: 'vs last month', value: change === null ? '—' : `${change >= 0 ? '+' : ''}${change.toFixed(0)}%` },
    { key: 'refunds', label: `Refunds, ${span} months`, value: total ? money(total.refunds, currency) : '—' },
  ];

  const saveIncome = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      { ...form, amount: Number(form.amount), currency: form.currency.trim().toUpperCase() },
      {
        onSuccess: () => {
          toast.success('Saved');
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <AccountingPageShell
      mock={false}
      title="Revenue"
      description="Platform sales read live from payments, net of refunds in the month they happened, plus marketplace commission and income received off-platform. Marketplace order payments and wallet top-ups are not revenue."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Receivables' }, { label: 'Revenue' }]}
      icon={TrendingUp}
      actions={
        <div className="flex flex-wrap gap-2">
          <select value={span} onChange={(e) => setSpan(Number(e.target.value) as (typeof RANGES)[number])} className={filterClass} aria-label="Range">
            {RANGES.map((r) => (
              <option key={r} value={r}>
                Last {r} months
              </option>
            ))}
          </select>
          {currencies.length > 1 && (
            <select value={currency} onChange={(e) => setPicked(e.target.value)} className={filterClass} aria-label="Currency">
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          )}
          <PermissionButton
            code={PERMISSIONS.accounting.manage}
            className="gap-2"
            onClick={() => setForm({ receivedOn: today(), kind: 'services', source: '', description: '', amount: '', currency, reference: '' })}
          >
            <Plus className="h-4 w-4" /> Record other income
          </PermissionButton>
        </div>
      }
      stats={stats}
    >
      {q.isError ? (
        <EmptyState label={`Could not load revenue: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading ? (
        <EmptyState label="Adding up revenue…" />
      ) : !data || data.rows.length === 0 ? (
        <EmptyState label="No revenue in this range yet." />
      ) : (
        <div className="space-y-4">
          <Panel title={`Net revenue by month (${currency})`}>
            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
              {lines.map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: dark ? l.dark : l.light }} />
                  {l.label}
                </span>
              ))}
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={dark ? '#2a2a28' : '#f3f4f6'} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: dark ? '#c3c2b7' : '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: dark ? '#c3c2b7' : '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                    width={64}
                    tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v))}
                  />
                  <Tooltip
                    cursor={{ fill: dark ? '#2a2a28' : '#f3f4f6' }}
                    formatter={(value: number, key: string) => [money(value, currency), REVENUE_LINES.find((l) => l.id === key)?.label ?? key]}
                    labelFormatter={(_, payload) => {
                      const m = payload?.[0]?.payload?.month as string | undefined;
                      return m ? `${monthLabel(m)} · net ${money(net(m), currency)}` : '';
                    }}
                  />
                  {lines.map((l, i) => (
                    <Bar
                      key={l.id}
                      dataKey={l.id}
                      stackId="net"
                      fill={dark ? l.dark : l.light}
                      stroke={dark ? '#1a1a19' : '#ffffff'}
                      strokeWidth={2}
                      radius={i === lines.length - 1 ? [4, 4, 0, 0] : 0}
                      maxBarSize={44}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="By product line">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="py-2 font-medium">Line</th>
                    {data.months.map((m) => (
                      <th key={m} className="py-2 text-right font-medium">
                        {monthLabel(m)}
                      </th>
                    ))}
                    <th className="py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {lines.map((l) => {
                    const cells = data.months.map((m) => data.rows.find((r) => r.month === m && r.line === l.id && r.currency === currency));
                    const sum = cells.reduce((n, c) => n + (c?.net ?? 0), 0);
                    return (
                      <tr key={l.id}>
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1.5 text-gray-800">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: dark ? l.dark : l.light }} />
                            {l.label}
                          </span>
                        </td>
                        {cells.map((c, i) => (
                          <td key={i} className="py-2 text-right text-gray-700" title={c ? `gross ${money(c.gross, currency)} · refunds ${money(c.refunds, currency)}` : undefined}>
                            {c ? money(c.net, currency) : '—'}
                          </td>
                        ))}
                        <td className="py-2 text-right font-medium text-gray-900">{money(sum, currency)}</td>
                      </tr>
                    );
                  })}
                  <tr className="font-medium">
                    <td className="py-2 text-gray-900">Net</td>
                    {data.months.map((m) => (
                      <td key={m} className="py-2 text-right text-gray-900">
                        {money(net(m), currency)}
                      </td>
                    ))}
                    <td className="py-2 text-right text-gray-900">{total ? money(total.net, currency) : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {data.noncash.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                Not counted above: gift card and voucher payments,{' '}
                {data.noncash
                  .filter((n) => n.currency === currency)
                  .reduce((s, n) => s + n.amount, 0)
                  .toLocaleString()}{' '}
                {currency}.
              </p>
            )}
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Top customers">
              {data.topCustomers.length === 0 ? (
                <p className="py-4 text-sm text-gray-500">No paying customers in this range.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {data.topCustomers.map((c) => (
                    <li key={`${c.kind}${c.id}${c.currency}`} className="flex items-center justify-between py-2 text-sm">
                      <Link
                        to={c.kind === 'user' ? `/admin/users/${c.id}` : `/admin/organizations/${c.id}`}
                        className="text-gray-800 hover:text-blue-700 hover:underline"
                      >
                        {c.name}
                        <span className="ml-1 text-xs text-gray-400">{c.kind === 'org' ? 'organisation' : ''}</span>
                      </Link>
                      <span className="text-gray-900">{money(c.amount, c.currency)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Other income">
              {data.otherIncome.length === 0 ? (
                <p className="py-4 text-sm text-gray-500">Nothing recorded. Use this for money received outside the platform.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {data.otherIncome.map((o) => (
                    <li key={o.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="text-gray-800">{o.source}</p>
                        <p className="text-xs text-gray-500">
                          {INCOME_KIND_LABEL[o.kind]} · {formatDay(o.receivedOn)}
                          {o.reference && ` · ${o.reference}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-900">{money(o.amount, o.currency)}</span>
                        <RowMenu
                          items={[
                            {
                              label: 'Edit',
                              icon: Pencil,
                              hidden: !canManage,
                              onSelect: () => setForm({ ...o, amount: String(o.amount) }),
                            },
                            { label: 'Delete', icon: Trash2, danger: true, hidden: !canManage, onSelect: () => setDeleting(o) },
                          ]}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Edit income' : 'Record other income'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={saveIncome} className="space-y-4">
              <p className="text-xs text-gray-500">For money received outside the platform. Loans and investment are not income; leave them out.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Received on">
                  <input type="date" required value={form.receivedOn} onChange={(e) => setForm({ ...form, receivedOn: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Kind">
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as OtherIncome['kind'] })} className={inputClass}>
                    {Object.entries(INCOME_KIND_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="From">
                  <input required value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inputClass} placeholder="Who paid" />
                </Field>
                <Field label="Reference">
                  <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Amount">
                  <input required type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Currency">
                  <input required maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                </Field>
              </div>
              <Field label="What for">
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this income entry?"
        description={deleting ? `${deleting.source}, ${money(deleting.amount, deleting.currency)} on ${formatDay(deleting.receivedOn)}.` : ''}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSuccess: () => toast.success('Deleted'), onError: (e) => toast.error((e as Error).message) });
          setDeleting(null);
        }}
      />
    </AccountingPageShell>
  );
}

